import { css } from '@emotion/css';

export const LINK_HOTSPOT_SIZE = 10;

/**
 * Both link handles — source hotspot on the right, arrival marker on
 * the left — straddle the bar's edge: half their diameter sits inside
 * the bar, half outside. Centering the handle *on* the edge makes the
 * dependency arrow read as a cable plugged into a proper port (edge =
 * port = endpoint) instead of a decorative circle floating in free
 * space. The overlay anchors arrows to the *outside* face of the
 * handle so the line doesn't disappear into the circle.
 *
 * Endpoint offset (distance from the bar's edge to the arrow's tip) =
 * the handle's radius, so the tip kisses the outer edge of the circle.
 */
export const LINK_HANDLE_OFFSET_PX = LINK_HOTSPOT_SIZE / 2;

/**
 * Magnet pull radius: how close (in px) the pointer must get to an
 * arrival handle's center before the handle snaps into "attract" mode
 * (visibly grows, preview arrow locks onto its center). Deliberately
 * larger than the handle's visible diameter so users don't have to
 * land the cursor *inside* the 10 px circle — they can aim roughly at
 * the target bar's left edge and still get a clean snap.
 */
export const LINK_MAGNET_RADIUS_PX = 24;

/** Scale factor applied to the arrival handle while magnetized. */
const MAGNET_SCALE = 1.6;

export const linkHotspotStyle = css({
  position: 'absolute',
  // Center on the bar's right edge: box from `right: -SIZE/2` gives
  // `[−SIZE/2, +SIZE/2]` → half inside bar, half outside. Requires
  // `box-sizing: border-box` so the 1.5 px border is included in the
  // 10 px total width — otherwise the rendered diameter becomes
  // 13 px and the handle sits biased 1.5 px inward.
  right: `-${LINK_HOTSPOT_SIZE / 2}px`,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${LINK_HOTSPOT_SIZE}px`,
  height: `${LINK_HOTSPOT_SIZE}px`,
  borderRadius: '50%',
  background: 'var(--streamlain-success-color)',
  border: '1.5px solid var(--streamlain-background-primary-color)',
  boxSizing: 'border-box',
  cursor: 'crosshair',
  opacity: 0,
  transition: 'opacity 0.12s ease-out',
  pointerEvents: 'auto',
  zIndex: 3,
});

/**
 * Mirror marker on the *left* edge of a bar: a visual target for
 * incoming dependency arrows. Three visibility states:
 *
 * - Hidden by default (opacity 0).
 * - Faintly visible on bar hover (via {@link barWithHotspotStyle}).
 * - Fully visible on every bar while a link-drag is in progress
 *   (any ancestor with `data-linking`) so the user can see every
 *   valid target, not just the one currently under the cursor.
 * - Enlarged + filled while magnetized (cursor inside
 *   {@link LINK_MAGNET_RADIUS_PX} of the handle's center). Scales
 *   via {@link MAGNET_SCALE} so the pull feels visible without
 *   moving the handle off the bar edge.
 */
export const linkArrivalStyle = css({
  position: 'absolute',
  // Mirror of linkHotspotStyle: half on the bar, half outside.
  left: `-${LINK_HOTSPOT_SIZE / 2}px`,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${LINK_HOTSPOT_SIZE}px`,
  height: `${LINK_HOTSPOT_SIZE}px`,
  borderRadius: '50%',
  // Theme-aware surface fill (white in light / blue-white viewport
  // scopes, dark surface in dark mode) so the idle handle reads as an
  // empty port — not a transparent hole showing bar green through it.
  // Magnetized state below swaps to success green for the "locked"
  // feedback.
  background: 'var(--streamlain-background-primary-color)',
  border: '1.5px solid var(--streamlain-success-color)',
  boxSizing: 'border-box',
  opacity: 0,
  // Scale + opacity both animate so the magnet pull reads as a single
  // motion; the transform origin is the center (default) which stays
  // pinned to the bar's edge.
  transition: 'opacity 0.12s ease-out, transform 0.12s ease-out',
  // Purely decorative — pointer events stay on the bar so neighbouring
  // empty-row pointerdown still routes correctly.
  pointerEvents: 'none',
  zIndex: 3,

  // Show on every bar during a link-drag. `[data-linking]` is set by
  // the gantt view host while a pending link endpoint signal is non-
  // null.
  '[data-linking] &': {
    opacity: 1,
  },

  // Magnetized: cursor is inside the pull radius. The bar renderer
  // flips `data-arrival-magnet` on the matching arrival span based on
  // the host's `magnetTargetRowId` signal.
  '&[data-arrival-magnet]': {
    opacity: 1,
    transform: `translateY(-50%) scale(${MAGNET_SCALE})`,
    background: 'var(--streamlain-success-color)',
  },
});

/**
 * Parent bar adds this class on hover to reveal the hotspot. Kept
 * independent of the bar class so later tokens stay scoped.
 */
export const barWithHotspotStyle = css({
  [`&:hover .${linkHotspotStyle}`]: {
    opacity: 1,
  },
  [`&:hover .${linkArrivalStyle}`]: {
    opacity: 1,
  },
});
